// service.js
import { BASE_URL, DIS_BASE_URL } from './constants';
import { tokenRefreshService } from './tokenRefreshService';

class ServiceHTTP {
  // Utility method to choose the correct base URL
  getFullUrl(url, useDispatcher = false) {
    return (useDispatcher ? DIS_BASE_URL : BASE_URL) + url;
  }

  // Helper method to handle 401 responses
  async handleResponse(response, originalRequest) {
    if (response.status === 401) {
      // Token expired, refresh and retry
      try {
        const retryResponse = await tokenRefreshService.handleUnauthorized(originalRequest);
        return retryResponse;
      } catch (error) {
        // If refresh fails, the error is thrown
        throw error;
      }
    }
    
    return response;
  }

  authenticate(userName, password) {
    console.log('base url :', BASE_URL);
    let _serStatusCode = 0;
    let data = JSON.stringify({
      "userId": userName,
      "password": password
    });
    let fullUrl = BASE_URL + 'Login';
    return fetch(fullUrl, {
      method: 'post',
      body: data,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      }
    }).then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    }).then((response) => {
      response.status = _serStatusCode;
      return response;
    }, (error) => {
      console.log('auth-error :', error);
      throw error; // Make sure to throw the error
    });
  }

  getAccessTokenWithRefreshToken(refreshToken) {
    let _serStatusCode = 0;
    let fullUrl = BASE_URL + 'oauth/token';
    
    const params = new URLSearchParams();
    params.append('grant_type', 'refresh_token');
    params.append('refresh_token', refreshToken);
    params.append('client_id', 'my-trusted-client');
    params.append('client_secret', 'secret');
    
    return fetch(fullUrl, {
      method: 'post',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: params.toString()
    }).then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    }).then((response) => {
      response.status = _serStatusCode;
      return response;
    }, (error) => {
      console.log('refreshtoken-error :', error);
      throw error; // Make sure to throw the error
    });
}

  get(url, headers = { "Accept": "application/json", "Content-Type": "application/json" }, token = undefined) {
    let _serStatusCode = 0;
    if (token) {
      headers.Authorization = 'Bearer ' + token;
    }
    let fullUrl = BASE_URL + url;
    
    const originalRequest = {
      method: 'GET',
      url,
      headers,
      isDispatcher: false
    };

    return fetch(fullUrl, {
      method: "GET",
      headers: headers
    })
    .then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    })
    .then(async (response) => {
      response.status = _serStatusCode;
      
      // Handle token refresh if needed
      if (response.status === 401) {
        return await this.handleResponse(response, originalRequest);
      }
      
      return response;
    })
    .catch((error) => {
      console.log('get-error :', error);
      throw error;
    });
  }

  post(url, data, headers = { "Content-Type": "application/json" }, token = undefined) {
    let _serStatusCode = 0;
    if (token) {
      headers.Authorization = 'Bearer ' + token;
    }
    let fullUrl = BASE_URL + url;
    
    const originalRequest = {
      method: 'POST',
      url,
      data,
      headers,
      isDispatcher: false
    };

    return fetch(fullUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data)
    })
    .then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    })
    .then(async (response) => {
      response.status = _serStatusCode;
      
      // Handle token refresh if needed
      if (response.status === 401) {
        return await this.handleResponse(response, originalRequest);
      }
      
      return response;
    })
    .catch((error) => {
      console.log('post-error :', error);
      throw error;
    });
  }

  postToDispatch(url, data, headers = { "Content-Type": "application/json" }, token = undefined) {
    let _serStatusCode = 0;
    if (token) {
      headers.Authorization = 'Bearer ' + token;
    }
    let fullUrl = DIS_BASE_URL + url;
    
    const originalRequest = {
      method: 'POST',
      url,
      data,
      headers,
      isDispatcher: true
    };

    return fetch(fullUrl, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(data)
    })
    .then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    })
    .then(async (response) => {
      response.status = _serStatusCode;
      
      // Handle token refresh if needed
      if (response.status === 401) {
        return await this.handleResponse(response, originalRequest);
      }
      
      return response;
    })
    .catch((error) => {
      console.log('post-error :', error);
      throw error;
    });
  }

  put(url, data, headers = { "Content-Type": "application/json" }, token = undefined) {
    let _serStatusCode = 0;
    let fullUrl = BASE_URL + url + ((token || '') === '' ? '' : '&access_token=' + token);
    
    const originalRequest = {
      method: 'PUT',
      url,
      data,
      headers,
      isDispatcher: false
    };

    return fetch(fullUrl, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(data)
    })
    .then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    })
    .then(async (response) => {
      response.status = _serStatusCode;
      
      // Handle token refresh if needed
      if (response.status === 401) {
        return await this.handleResponse(response, originalRequest);
      }
      
      return response;
    })
    .catch((error) => {
      console.log('put-error :', error);
      throw error;
    });
  }

  delete(url, headers = { "Content-Type": "application/json" }, token = undefined) {
    let _serStatusCode = 0;
    let fullUrl = BASE_URL + url + ((token || '') === '' ? '' : '&access_token=' + token);
    
    const originalRequest = {
      method: 'DELETE',
      url,
      headers,
      isDispatcher: false
    };

    return fetch(fullUrl, {
      method: 'DELETE',
      headers: headers
    })
    .then((resp) => {
      _serStatusCode = resp.status;
      return resp.json();
    })
    .then(async (response) => {
      response.status = _serStatusCode;
      
      // Handle token refresh if needed
      if (response.status === 401) {
        return await this.handleResponse(response, originalRequest);
      }
      
      return response;
    })
    .catch((error) => {
      console.log('del-error :', error);
      throw error;
    });
  }
}

export const serviceHTTP = new ServiceHTTP();